import React, { useContext, useMemo } from 'react'

import { Box, DisplayPosition } from '@gousto-internal/citrus-react'
import { useDispatch, useSelector } from 'react-redux'

import { FlexPortionTileBadge } from '@features/recipe-tile'
import type { RecipeGridContentBlock } from '@library/api-menu-experience'
import { FlexPortionEditorBoundary, RecipeGrid } from '@library/menu-page'
import type { RecipeUUID } from '@library/menu-service'

import {
  WantToSeeMore,
  getClickAllRecipesTrackingAction,
} from 'routes/Menu/MenuRecipesPage/CTAToAllRecipes'
import {
  CaloriesTutorial,
  useShouldShowCaloriesTutorial,
} from 'routes/Menu/components/MenuPreferences'
import { CollectionId, CollectionSlug } from 'routes/Menu/domains/collections'
import { useSetBrowserCTAVisibility } from 'routes/Menu/domains/menu'
import { getBasketDate } from 'selectors/basket'

import { MenuBrandingContext } from '../menuBrandingContext'
import { useMenuExperienceCategoryNavigation } from '../menuExperienceNavigationContext'
import { useMenuExperienceRecipeDetails } from '../menuExperienceRecipeDetailContext'
import { useRecipeGridCustomisation } from '../recipeGridCustomisationContext'
import { useRecipeGridBasket } from './useRecipeGridBasket'
import { useRecipeGridCustomisationEducation } from './useRecipeGridCustomisationEducation'
import { useRecipeGridFlex } from './useRecipeGridFlex'
import { useRecipeGridFlexEducation } from './useRecipeGridFlexEducation'

type RecipeGridContentBlockWrapperProps = {
  contentBlock: RecipeGridContentBlock
  collectionId?: string
}

export const RecipeGridContentBlockWrapper = ({
  contentBlock,
  collectionId,
}: RecipeGridContentBlockWrapperProps) => {
  const dispatch = useDispatch()
  const { textColor } = useContext(MenuBrandingContext)
  const { categories = [], selectCategory } = useMenuExperienceCategoryNavigation()
  const { menuId, menuUrn, openRecipeDetail } = useMenuExperienceRecipeDetails()
  const deliveryDate = useSelector(getBasketDate)
  const shouldShowCaloriesTutorial = useShouldShowCaloriesTutorial(collectionId)
  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()
  const {
    getRecipeGridItem,
    getRecipeCustomisationData,
    openRecipeCustomisations,
    closeRecipeCustomisations,
    selectRecipeCustomisation,
    retryRecipeCustomisation,
    refreshCustomisedRecipeGridItem,
    reconcileCustomisedRecipeGridItemAfterRemoval,
  } = useRecipeGridCustomisation()
  const recipeGridItems = useMemo(
    () => contentBlock.items.map(getRecipeGridItem),
    [contentBlock.items, getRecipeGridItem],
  )
  const recipeGridItemById = useMemo(
    () => new Map(recipeGridItems.map((item) => [item.id, item])),
    [recipeGridItems],
  )
  const { recipeIdForEducation, closeEducation } = useRecipeGridCustomisationEducation(contentBlock)
  const {
    closeFlexPortionEditor,
    getFlexPortionData,
    openFlexPortionEditor,
    removeFlexRecipe,
    selectFlexPortions,
    updateFlexPortions,
  } = useRecipeGridFlex({ items: recipeGridItems })
  const { closeFlexEducationTooltip, flexEducationRecipeId, onRecipeAdded } =
    useRecipeGridFlexEducation()
  const { getRecipeTileState, onAddClick, onRemoveClick } = useRecipeGridBasket(recipeGridItems, {
    onRecipeAdded,
  })

  const renderFlexPortionBadge = (recipeId: RecipeUUID) => {
    const { isVisible, quantity, numPortions } = getFlexPortionData(recipeId)

    if (!isVisible) {
      return null
    }

    return (
      <FlexPortionTileBadge
        recipeCount={quantity}
        portionCount={numPortions}
        onEdit={() => openFlexPortionEditor(recipeId)}
        showEducationTooltip={flexEducationRecipeId === recipeId}
        onCloseEducationTooltip={closeFlexEducationTooltip}
      />
    )
  }

  const renderFlexPortionEditor = (recipeId: RecipeUUID) => {
    const { isEditorOpen, quantity, numPortions } = getFlexPortionData(recipeId)
    const recipe = recipeGridItemById.get(recipeId)

    if (!isEditorOpen || !recipe) {
      return null
    }

    const handleUpdateRecipePortions = (
      updatedRecipeId: RecipeUUID,
      updatedNumPortions: number,
    ) => {
      updateFlexPortions(updatedRecipeId, updatedNumPortions)
      void refreshCustomisedRecipeGridItem(updatedRecipeId, updatedNumPortions)
    }

    const handleRemoveRecipe = (removedRecipeId: RecipeUUID) => {
      removeFlexRecipe(removedRecipeId)
      reconcileCustomisedRecipeGridItemAfterRemoval(removedRecipeId)
    }

    return (
      <FlexPortionEditorBoundary
        // The endpoint requires mutually exclusive UUID/URN identifiers.
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(recipe.urn ? { recipeUrn: recipe.urn } : { recipeId })}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(menuUrn ? { menuUrn } : { menuId })}
        deliveryDate={deliveryDate}
        isOpen
        recipeCount={quantity}
        numPortions={numPortions}
        onClose={closeFlexPortionEditor}
        onRemoveRecipe={() => handleRemoveRecipe(recipeId)}
        onUpdatePortions={(updatedNumPortions) =>
          handleUpdateRecipePortions(recipeId, updatedNumPortions)
        }
        onSelectPortions={(updatedNumPortions) => selectFlexPortions(recipeId, updatedNumPortions)}
        variant="floating-tile"
      />
    )
  }

  const onRecipeClick = (recipeId: RecipeUUID, recipePosition: number) => {
    const recipe = recipeGridItemById.get(recipeId)

    if (!recipe) {
      return
    }

    openRecipeDetail({
      recipeId,
      recipeUrn: recipe.urn,
      title: recipe.title,
      position: recipePosition.toString(),
      source: 'recipe_grid',
      onRecipePortionsUpdated: refreshCustomisedRecipeGridItem,
    })
  }

  const handleRemoveClick = (recipeId: RecipeUUID, recipePosition: number) => {
    onRemoveClick(recipeId, recipePosition)
    reconcileCustomisedRecipeGridItemAfterRemoval(recipeId)
  }

  const onWantToSeeMoreClick = () => {
    const allRecipesCategory = categories.find(({ slug }) => slug === CollectionSlug.AllRecipes)

    if (!allRecipesCategory) {
      return
    }

    selectCategory(allRecipesCategory, 'recipe_grid')
    dispatch(getClickAllRecipesTrackingAction())
  }

  const supplementaryItems = [
    ...(shouldShowCaloriesTutorial
      ? [
          {
            id: 'calories-tutorial',
            position: 5 as const,
            content: (
              <Box position={DisplayPosition.Relative} width="100%">
                <CaloriesTutorial />
              </Box>
            ),
          },
        ]
      : []),
    ...(collectionId === CollectionId.Recommendations
      ? [
          {
            id: 'want-to-see-more',
            position: 'end' as const,
            content: <WantToSeeMore onClick={onWantToSeeMoreClick} />,
          },
        ]
      : []),
  ]

  return (
    <RecipeGrid
      contentBlock={contentBlock}
      recipeCountTextColor={textColor}
      supplementaryItems={supplementaryItems}
      getRecipeGridItem={getRecipeGridItem}
      getRecipeTileState={getRecipeTileState}
      getRecipeCustomisationData={getRecipeCustomisationData}
      customisationEducationRecipeId={recipeIdForEducation}
      onRecipeClick={onRecipeClick}
      onAddClick={onAddClick}
      onRemoveClick={handleRemoveClick}
      onBlockedAddAttempt={setBrowserCTAVisible}
      onCustomiseOpen={openRecipeCustomisations}
      onCustomiseClose={closeRecipeCustomisations}
      onCustomisationSelect={selectRecipeCustomisation}
      onCustomisationRetry={retryRecipeCustomisation}
      onCustomisationEducationClose={closeEducation}
      renderFlexPortionBadge={renderFlexPortionBadge}
      renderFlexPortionEditor={renderFlexPortionEditor}
    />
  )
}
